// =====================================================
// DIGITAL LEARNING MANAGER - Types
// READ-ONLY — All types match the real Supabase schema
// =====================================================

// =====================================================
// USER & AUTH
// =====================================================

export type UserRole = 'super_admin' | 'org_admin' | 'trainer' | 'learner';

// Roles qui peuvent acceder au Manager
export const MANAGER_ALLOWED_ROLES: UserRole[] = ['super_admin', 'trainer', 'org_admin'];

// URL de redirection pour les roles non autorises
export const LMS_URL = 'https://cours.ondako.fr';

export interface User {
  id: string;
  email: string;
  role: UserRole;
  organizationId: string | null;
  displayName?: string;
  avatarUrl?: string;
  lastLoginAt?: Date;
  createdAt: Date;
}

// =====================================================
// ORGANIZATIONS
// =====================================================

export type OrganizationStatus = 'active' | 'inactive';

export interface Organization {
  id: string;
  name: string;
  slug?: string;
  brandColor?: string;
  logoUrl?: string;
  maxLearners: number | null;
  // CRM columns (added via migration 20260305)
  status: OrganizationStatus;
  contactName?: string;
  contactEmail?: string;
  contactPhone?: string;
  address?: string;
  notes?: string;
  createdAt: Date;
  updatedAt: Date;
}

export interface OrganizationWithStats extends Organization {
  learnersCount: number;
  completedCount: number;
  completionRate: number;
}

// =====================================================
// STRUCTURES (minimal — real schema: id, organization_id, name)
// =====================================================

export interface Structure {
  id: string;
  organizationId: string;
  name: string;
}

// =====================================================
// GROUPS
// =====================================================

export interface Group {
  id: string;
  organizationId: string;
  name: string;
  description?: string;
  createdAt: Date;
  updatedAt: Date;
}

// =====================================================
// LEARNERS
// =====================================================

export type LearnerStatus = 'active' | 'inactive';

export interface Learner {
  id: string;
  organizationId: string;
  structureId?: string;
  lmsUserId?: string;
  email: string;
  fullname: string;
  jobTitle?: string;
  department?: string;
  status: LearnerStatus;
  enrolledAt?: Date;
  completedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface LearnerWithDetails extends Learner {
  groups?: Group[];
  lastSignInAt?: Date; // from get_learners_with_stats RPC
  enrollments?: EnrollmentWithProgress[];
  overallProgress: number; // computed from course_progress
}

// =====================================================
// COURSES (real schema: title, estimated_duration_minutes)
// =====================================================

export interface Course {
  id: string;
  organizationId: string;
  title: string;
  description?: string;
  thumbnailUrl?: string;
  estimatedDurationMinutes?: number;
  isPublished: boolean;
  certificateEnabled: boolean;
  prerequisiteCourseId?: string;
  createdAt: Date;
  updatedAt: Date;
}

// =====================================================
// ENROLLMENTS & PROGRESS
// Real schema: enrollments has NO status/progress_percent
// Completion = course_progress.completed_at IS NOT NULL
// =====================================================

export interface Enrollment {
  id: string;
  learnerId: string;
  courseId: string;
  dueDate?: Date;
  enrolledAt: Date;
  createdAt: Date;
}

export interface EnrollmentWithProgress extends Enrollment {
  course?: Course;
  courseProgress?: CourseProgress;
  // Computed from course_progress
  progressPercent: number;
  isCompleted: boolean;
  completedAt?: Date;
}

export interface CourseProgress {
  id: string;
  enrollmentId: string;
  progressPercent: number;
  completedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

// =====================================================
// DASHBOARD STATS (used by RPC functions)
// =====================================================

export interface DashboardStats {
  organizationsCount: number;
  activeOrganizationsCount: number;
  learnersCount: number;
  activeLearnersCount: number;
  completedLearnersCount: number;
  overallCompletionRate: number;
}

// Stats from RPC get_enrollment_status_distribution
export interface EnrollmentStatusDistribution {
  status: string; // 'not_started' | 'in_progress' | 'completed'
  count: number;
}

// Stats from RPC get_monthly_enrollment_stats
export interface MonthlyEnrollmentStats {
  monthKey: string;
  inscriptions: number;
  completions: number;
}

// Stats from RPC get_top_courses_by_enrollments
export interface TopCourse {
  courseName: string;
  inscrits: number;
}

// Stats from RPC get_group_completion_rates
export interface GroupCompletionRate {
  groupName: string;
  totalEnrollments: number;
  completedEnrollments: number;
  taux: number;
}

// =====================================================
// FILTERS
// =====================================================

export interface LearnerFilters {
  organizationId?: string;
  structureId?: string;
  status?: LearnerStatus;
  search?: string;
}
